import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import SeasonSelector from '../components/SeasonSelector';
import PlantingFlow from '../components/PlantingFlow';
import { STATUS_LABELS, STATUS_COLORS } from './PlantingDetailPage';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR') : '');

const STATUS_FILTERS = ['', 'planned', 'planted', 'germinated', 'growing', 'ready', 'harvested'];

export default function PlantingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seasonFilter, setSeasonFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [showFlow, setShowFlow] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (seasonFilter) params.set('seasonId', seasonFilter);
    if (statusFilter) params.set('status', statusFilter);
    const qs = params.toString() ? `?${params}` : '';
    api.get(`/plantings${qs}`).then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, [seasonFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const advanceStatus = async (item, status) => {
    try {
      await api.put(`/plantings/${item.id}/status`, { status });
      toast.success(t('settings.saved'));
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const toHarvest = (item) => {
    sessionStorage.setItem('harvestFromPlanting', JSON.stringify({
      plantingId: item.id, fieldId: item.field_id, productName: item.product_name, seasonId: item.season_id,
    }));
    window.location.href = '/harvest';
  };
  const toWorkOrder = (item) => {
    sessionStorage.setItem('workOrderFromPlanting', JSON.stringify({ plantingId: item.id, fieldId: item.field_id, title: `${item.product_name} - ${item.field_name || ''}` }));
    window.location.href = '/work-orders';
  };

  // Action buttons per status. Each: {label, onClick}. `stop` prevents card navigation.
  const actionsFor = (item) => {
    const adv = (s) => (e) => { e.stopPropagation(); advanceStatus(item, s); };
    switch (item.status) {
      case 'planned': return [{ label: '✅ Ekimi Başlat', bg: '#1976d2', onClick: adv('planted') }];
      case 'planted': return [{ label: '🌿 Çıkış Yaptı', bg: '#00897b', onClick: adv('germinated') }];
      case 'germinated': return [
        { label: '📋 İş Emri', bg: '#607d8b', onClick: (e) => { e.stopPropagation(); toWorkOrder(item); } },
        { label: '🌱 Büyüyor', bg: '#388e3c', onClick: adv('growing') }];
      case 'growing': return [
        { label: '📋 İş Emri', bg: '#607d8b', onClick: (e) => { e.stopPropagation(); toWorkOrder(item); } },
        { label: '⭐ Hasada Hazır', bg: '#f57c00', onClick: adv('ready') }];
      case 'ready': return [{ label: '🌾 Hasat Kaydet', bg: '#2e7d32', onClick: (e) => { e.stopPropagation(); toHarvest(item); } }];
      case 'harvested': return [{ label: '👁️ Detay', bg: '#795548', onClick: (e) => { e.stopPropagation(); navigate(`/plantings/${item.id}`); } }];
      case 'failed': return [{ label: '🔄 Yeniden Dene', bg: '#1976d2', onClick: adv('planted') }];
      default: return [];
    }
  };

  return (
    <div>
      <h2 className="section-title">{t('plantings.title')}</h2>

      <SeasonSelector value={seasonFilter} onChange={setSeasonFilter} />

      <div className="chip-group">
        {STATUS_FILTERS.map(s => (
          <button key={s} className={`chip${statusFilter === s ? ' active' : ''}`}
            onClick={() => setStatusFilter(s)}>{s === '' ? 'Tümü' : STATUS_LABELS[s]}</button>
        ))}
      </div>

      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? (
            <div className="empty-state"><div className="icon">🌱</div><p>{t('common.noData')}</p></div>
          ) : items.map(item => (
            <div key={item.id} onClick={() => navigate(`/plantings/${item.id}`)}
              className="txn-item" style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="txn-name">{item.product_name}{item.variety && <span style={{ fontWeight: 400, color: '#999', fontSize: '0.82rem' }}> ({item.variety})</span>}</div>
                  <div className="txn-meta">
                    {item.field_name || '—'}{item.field_size ? ` (${item.field_size} da)` : ''}
                    {item.season_name && ` · ${item.season_name}`}
                  </div>
                  <div className="txn-meta">
                    {fmtDate(item.planting_date)}{item.expected_harvest_date && ` → ${fmtDate(item.expected_harvest_date)}`}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, color: '#fff', background: STATUS_COLORS[item.status] || '#999' }}>
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  {actionsFor(item).map((a, i) => (
                    <button key={i} onClick={a.onClick}
                      style={{ background: a.bg, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost summary + counts */}
              {(item.total_cost > 0 || item.labour_count > 0 || item.expense_count > 0) && (
                <div style={{ display: 'flex', gap: 10, marginTop: 8, paddingTop: 6, borderTop: '1px solid #f2f2f2', fontSize: '0.72rem', color: '#666', flexWrap: 'wrap', alignItems: 'center' }}>
                  {item.total_seed_cost > 0 && <span>🌰 {fmt(item.total_seed_cost)}</span>}
                  {item.total_labour_cost > 0 && <span>👷 {fmt(item.total_labour_cost)}</span>}
                  {item.total_expense_cost > 0 && <span>🛒 {fmt(item.total_expense_cost)}</span>}
                  <span style={{ fontWeight: 800, color: '#1c3a1c' }}>Toplam: {fmt(item.total_cost)}</span>
                  <span style={{ marginLeft: 'auto', color: '#aaa' }}>
                    {item.labour_count > 0 && `${item.labour_count} işçilik`}
                    {item.labour_count > 0 && item.expense_count > 0 && ' · '}
                    {item.expense_count > 0 && `${item.expense_count} gider`}
                  </span>
                </div>
              )}
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => setShowFlow(true)}>+</button>

      {showFlow && (
        <PlantingFlow
          prefill={{ seasonId: seasonFilter || '' }}
          onClose={() => setShowFlow(false)}
          onSaved={(id) => { setShowFlow(false); load(); if (id) navigate(`/plantings/${id}`); }}
        />
      )}
    </div>
  );
}
